import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';

const BASE = SERVER_URL;

export type QuestId = 'share' | 'play' | 'win';

export interface DailyQuest {
  quest: QuestId;
  target: number;
  progress: number;
  gems: number;
  coins: number;
  claimed: boolean;
  claimable: boolean;
}
export interface QuestClaim {
  gems: number;
  coins: number;
  balanceGems: number;
  balanceCoins: number;
}

async function req<T>(method: string, path: string): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
  });
  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    try { const j = await res.json(); code = j?.error ?? code; } catch { /* */ }
    throw new Error(code);
  }
  return (await res.json()) as T;
}

export const questApi = {
  status: () => req<{ quests: DailyQuest[] }>('GET', '/me/quests'),
  claim: (quest: QuestId) => req<QuestClaim>('POST', `/me/quests/${quest}/claim`),
};

/** Open the native/web share sheet. Resolves true ONLY when the share sheet was
 * actually completed (a target was chosen) — cancelling returns false so the
 * reward isn't granted for just opening then bailing. `url` defaults to the
 * landing page; pass a referral link to share that instead. */
export async function shareGame(text: string, url = 'https://dontmiss.games'): Promise<boolean> {
  // Native: Share.share() resolves only when the sheet completes; it throws on
  // cancel → no reward.
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() !== 'web') {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title: "Don't Miss!", text, url });
      return true;
    }
  } catch {
    return false; // cancelled / failed → no reward
  }
  // Web: require a completed Web Share; otherwise open a composer (best signal
  // available on desktop — web gameplay is gated anyway, so this path is rare).
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title: "Don't Miss!", text, url });
      return true;
    }
  } catch {
    return false; // user dismissed
  }
  window.open(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${text} ${url}`)}`,
    '_blank',
    'noopener',
  );
  return true;
}
